import { Add, DeleteForever } from "@mui/icons-material";
import {
    Button,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    Hidden,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { navigate } from "@reach/router";
import gql from "graphql-tag";
import { useState } from "react";
import { useMutation } from "react-apollo";
import { toast, ToastContainer } from "react-toastify";
import styled from "styled-components";
import { v4 } from "uuid";
import { THEO_ID } from "../../views/App/AppRoot";
import { GET_DATACENTER } from "../../views/App/TLADatacenter";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";
import JSONInput, { isValidJSON } from "../JSONInput/JSONInput";
import DropdownSelector from "../Regions/DropdownSelector";
import SwitchWithTitle from "../SwitchWithTitle/SwitchWithTitle";
import {
    CreateTLADatacenterMutation,
    CreateTLADatacenterMutationVariables,
} from "./__generated__/CreateTLADatacenterMutation";
import { UpdateDatacenter, UpdateDatacenterVariables } from "./__generated__/UpdateDatacenter";
import TLALabelsManager from "./TLALabelsManager";
import { UPDATE_DATACENTER } from "./TLAUpdateDatacenterIdDialog";

export interface TLACertificate {
    id: string;
    name: string;
    source: string;
}

export interface TLADatacenterInput {
    id: string;
    name: string;
    tlaName: string;
    tlaDescription: string;
    tlaDomain: string;
    tlaHostId: string;
    tlaTls: boolean;
    tlaHttp3: boolean;
    tlaOverride: string;
    tlaRegion: string;
    tlaProvider: string;
    fqdn?: TLADatacenterFQDN;
    tlaLetsEncrypt: boolean;
    labels: string[];
    tlaCertificates: TLACertificate[];
}

export interface TLADatacenterFQDN {
    ingest: string;
    origins: string;
}

const SectionTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
`;

const onPremTlaLabels = ["live.theostream.features=on-prem", "live.theostream.device-type="];

interface Props {
    id?: string;
    values: TLADatacenterInput;
    onUpdateSuccess?: () => void;
}

export const CREATE_TLA_DATACENTER_MUTATION = gql`
    mutation CreateTLADatacenterMutation($input: CreateDatacenterInput!) {
        createTLADatacenter(input: $input) {
            id
        }
    }
`;

export default function TLAManageDatacenterForm({ values, id: existingId, onUpdateSuccess }: Props): JSX.Element {
    const [id, setId] = useState<string>(values.id);
    const [name, setName] = useState<string>(values.name);
    const [tlaDescription, setTlaDescription] = useState<string>(values.tlaDescription);
    const [tlaDomain, setTlaDomain] = useState<string>(values.tlaDomain);
    const [tlaHostId, setTlaHostId] = useState<string>(values.tlaHostId);
    const [tlaRegion, setTlaRegion] = useState<string>(values.tlaRegion);
    const [tlaProvider, setTlaProvider] = useState<string>(values.tlaProvider);
    const [labels, setLabels] = useState<Record<string, string>>(arrayToRecord(values.labels));

    const [tlaTls, setTlatls] = useState<boolean>(values.tlaTls);
    const [tlaHttp3, setTlaHttp3] = useState<boolean>(values.tlaHttp3);
    const [tlaLetsEncrypt, setTlaLetsEncrypt] = useState<boolean>(values.tlaLetsEncrypt);
    const [tlaCertificates, setTlaCertificates] = useState<TLACertificate[]>(values.tlaCertificates);

    const [override, setOverride] = useState<string>(values.tlaOverride);
    const [fqdn, setFqdn] = useState<TLADatacenterFQDN | undefined>(values.fqdn);
    const [labelsOk, setLabelsOk] = useState<boolean>(true);

    const [firstSubmitDone, setFirstSubmitDone] = useState<boolean>(false);
    const [onPrem, setOnPrem] = useState<boolean>(false);

    const [createDatacenter, { loading: loadingCreate }] = useMutation<
        CreateTLADatacenterMutation,
        CreateTLADatacenterMutationVariables
    >(CREATE_TLA_DATACENTER_MUTATION);

    const [updateDatacenter, { loading: loadingDatacenter }] = useMutation<UpdateDatacenter, UpdateDatacenterVariables>(
        UPDATE_DATACENTER,
    );

    function validId(): boolean {
        if (existingId) {
            return true;
        }
        if (id.includes(" ")) {
            return false;
        }
        if (id.trim().length === 0) {
            return false;
        }
        return true;
    }

    function validName(): boolean {
        if (existingId) {
            return true;
        }
        if (name.trim().length === 0) {
            return false;
        }
        return true;
    }

    function arrayToRecord(lbls: string[]): Record<string, string> {
        const record: Record<string, string> = {};

        lbls.forEach((label) => (record[v4()] = label));
        return record;
    }

    function onChangeLabel(idx: string, value: string) {
        const copyLabels = labels;
        copyLabels[idx] = value;
        setLabels(copyLabels);
        setLabelsOk(validLabels(Object.values(copyLabels)));
    }

    function validLabels(lbls: string[]) {
        let isValid = true;
        lbls.forEach((label) => {
            if (!regex.test(label)) {
                isValid = false;
            }
        });
        return isValid;
    }

    function updateOnPremLabels() {
        const checked = !onPrem;
        setOnPrem(checked);
        if (checked) {
            const copyLabels = { ...labels };

            onPremTlaLabels.forEach((label) => {
                copyLabels[v4()] = label;
            });

            setLabels(copyLabels);
            setLabelsOk(validLabels(Object.values(copyLabels)));
        } else {
            // remove labels
            const newLabels: Record<string, string> = {};

            // Keep only labels that are NOT in the predefined list
            for (let key in labels) {
                if (!onPremTlaLabels.includes(labels[key])) {
                    newLabels[key] = labels[key];
                }
            }

            setLabels(newLabels);
            setLabelsOk(validLabels(Object.values(newLabels)));
        }
    }

    const regex = /([A-z0-9/+-.,])*=([A-z0-9/+-.,])*/;

    function onRemoveLabel(idx: string) {
        const newList: Record<string, string> = {};
        for (let key in labels) {
            if (key !== idx) {
                newList[key] = labels[key];
            }
        }
        setLabels(newList);
        setLabelsOk(validLabels(Object.values(newList)));
    }

    // function validTLAHostId(): boolean {
    //     if (tlaHostId.includes(" ")) {
    //         return false;
    //     }
    //     if (tlaHostId.trim().length === 0) {
    //         return false;
    //     }
    //     return true;
    // }

    // function validDomain(): boolean {
    //     return tlaDomain.trim().length !== 0;
    // }

    function isValid(): boolean {
        return validId() && validName() && (override === "" || (override !== "" && isValidJSON(override)));
    }

    async function onSubmitClick(): Promise<void> {
        setFirstSubmitDone(true);
        if (isValid()) {
            try {
                if (!existingId) {
                    const res = await createDatacenter({
                        variables: {
                            input: {
                                id,
                                name,
                                tlaDescription,
                                tlaDomain: tlaDomain.trim().length > 0 ? tlaDomain : undefined,
                                tlaName: name,
                                tlaHostId: tlaHostId.trim().length > 0 ? tlaHostId : undefined,
                                tlaProvider: tlaProvider.trim().length > 0 ? tlaProvider : undefined,
                                tlaRegion: tlaRegion.trim().length > 0 ? tlaRegion : undefined,
                                tlaHttp3,
                                labels: Object.values(labels),
                                tlaTLS: tlaTls,
                                override: override.trim().length > 0 ? override : undefined,
                                fqdn: typeof fqdn !== "undefined" ? fqdn : undefined,
                                certificates: !tlaLetsEncrypt
                                    ? tlaCertificates.map(({ name, source }) => ({ name, source }))
                                    : undefined,
                                tlaLetsEncrypt,
                            },
                        },
                    });
                    navigate(`/app/${THEO_ID}/tla/data-centers/${res.data!.createTLADatacenter.id}`);
                    toast.success("TLA data center created", TOAST_SETTINGS);
                } else {
                    await updateDatacenter({
                        variables: {
                            input: {
                                id: existingId,
                                certificates: !tlaLetsEncrypt
                                    ? tlaCertificates.map(({ name, source }) => ({ name, source }))
                                    : undefined,
                                tlaDomain: tlaDomain.trim().length > 0 ? tlaDomain : undefined,
                                tlaDescription: tlaDescription.trim().length > 0 ? tlaDescription : undefined,
                                tlaHostId: tlaHostId.trim().length > 0 ? tlaHostId : undefined,
                                tlaProvider: tlaProvider.trim().length > 0 ? tlaProvider : undefined,
                                tlaRegion: tlaRegion.trim().length > 0 ? tlaRegion : undefined,
                                tlaHttp3,
                                tlaTLS: tlaTls,
                                labels: Object.values(labels),
                                override: override.trim().length > 0 ? override : undefined,
                                fqdn: typeof fqdn !== "undefined" ? fqdn : undefined,
                                tlaLetsEncrypt,
                            },
                        },
                        refetchQueries: [
                            {
                                query: GET_DATACENTER,
                                variables: {
                                    id: existingId,
                                },
                            },
                        ],
                    });
                    if (onUpdateSuccess) {
                        onUpdateSuccess();
                    }
                }
            } catch (e) {
                console.log(e);
                toast.error("Something went wrong", TOAST_SETTINGS);
            }
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <SectionTitle>Basic information</SectionTitle>
            </Grid>
            {!existingId && (
                <>
                    <Grid item xs={12} md={existingId ? 12 : 6}>
                        <FormControl fullWidth>
                            <HESPFormLabel
                                label="ID"
                                description="Required: ID that will be visible to the customers"
                            />
                            <TextField
                                variant="outlined"
                                fullWidth
                                defaultValue={name}
                                placeholder="ID"
                                onChange={(e: any) => setId(e.target.value)}
                            />
                            <FormHelperText>Should not contain spaces</FormHelperText>
                            {!validId() && firstSubmitDone && (
                                <Typography variant="subtitle2" color="error">
                                    Please enter a valid ID
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Hidden mdDown>
                        <Grid item md={existingId ? 12 : 6}></Grid>
                    </Hidden>
                    <Grid item xs={12} md={existingId ? 12 : 6}>
                        <FormControl fullWidth>
                            <HESPFormLabel
                                label="Name"
                                description="Required: Name that will be visible to the customers"
                            />
                            <TextField
                                variant="outlined"
                                fullWidth
                                defaultValue={name}
                                placeholder="Name"
                                onChange={(e: any) => setName(e.target.value)}
                            />
                            {!validName() && firstSubmitDone && (
                                <Typography variant="subtitle2" color="error">
                                    Please enter a valid name
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Hidden mdDown>
                        <Grid item md={existingId ? 12 : 6}></Grid>
                    </Hidden>
                </>
            )}
            <Grid item xs={12} md={existingId ? 12 : 6}>
                <FormControl fullWidth>
                    <HESPFormLabel
                        label="Description"
                        description="Optional: Description that will be used internally in TLA environment"
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={tlaDescription}
                        placeholder="Description"
                        onChange={(e: any) => setTlaDescription(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={existingId ? 12 : 6}></Grid>
            </Hidden>
            <Grid item xs={12} md={existingId ? 12 : 6}>
                <FormControl fullWidth>
                    <HESPFormLabel
                        label="Domain"
                        description="Domain to be use in TLA environment (required for THEOlive, optional for THEOads)"
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={tlaDomain}
                        placeholder="Domain"
                        onChange={(e: any) => setTlaDomain(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={existingId ? 12 : 6}></Grid>
            </Hidden>
            <Grid item xs={12} md={existingId ? 12 : 6}>
                <FormControl fullWidth>
                    <HESPFormLabel
                        label="Host ID"
                        description="Host ID to be use in TLA environment  (required for THEOlive, optional for THEOads)"
                    />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={tlaHostId}
                        placeholder="Host ID"
                        onChange={(e: any) => setTlaHostId(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={existingId ? 12 : 6}></Grid>
            </Hidden>
            <Grid item xs={12} md={existingId ? 12 : 6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Region" description="Region to be use in TLA environment" />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={tlaRegion}
                        placeholder="Region"
                        onChange={(e: any) => setTlaRegion(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={existingId ? 12 : 6}></Grid>
            </Hidden>
            <Grid item xs={12} md={existingId ? 12 : 6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Provider" description="Provider to be use in TLA environment" />
                    <DropdownSelector
                        selectedKey={tlaProvider}
                        items={[
                            {
                                key: "generic",
                                value: "generic",
                            },
                            {
                                key: "oci",
                                value: "oci",
                            },
                            {
                                key: "hetzner",
                                value: "hetzner",
                            },
                            {
                                key: "linode",
                                value: "linode",
                            },
                            {
                                key: "digitalocean",
                                value: "digitalocean",
                            },
                            {
                                key: "aws",
                                value: "aws",
                            },
                            {
                                key: "vagrant",
                                value: "vagrant",
                            },
                            {
                                key: "theo-office",
                                value: "theo-office",
                            },
                            {
                                key: "sf-lab-on-prem",
                                value: "sf-lab-on-prem",
                            },
                        ]}
                        onChangeSelected={(val: string) => setTlaProvider(val)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={existingId ? 12 : 6}></Grid>
            </Hidden>
            <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                    <HESPFormLabel label="TLS" />
                    <SwitchWithTitle title="Use TLS" checked={tlaTls} onChange={() => setTlatls(!tlaTls)} />
                </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                    <HESPFormLabel label="HTTP/3" />
                    <SwitchWithTitle title="Use HTTP/3" checked={tlaHttp3} onChange={() => setTlaHttp3(!tlaHttp3)} />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={existingId ? 12 : 6}></Grid>
            </Hidden>
            <Grid item xs={12}>
                <FormControl>
                    <HESPFormLabel label="RTMP push enabled?" />
                    <SwitchWithTitle
                        title="Enable RTMP push"
                        checked={typeof fqdn !== "undefined"}
                        onChange={() => setFqdn(typeof fqdn === "undefined" ? { ingest: "", origins: "" } : undefined)}
                    />
                </FormControl>
            </Grid>
            {typeof fqdn !== "undefined" && (
                <>
                    <Grid item xs={12} md={7}>
                        <HESPFormLabel label="FQDN: ingest" />
                        <TextField
                            variant="outlined"
                            fullWidth
                            defaultValue={fqdn.ingest}
                            placeholder="Ingest"
                            onChange={(e: any) => setFqdn({ ...fqdn, ingest: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <HESPFormLabel label="FQDN: origins" />
                        <TextField
                            variant="outlined"
                            fullWidth
                            defaultValue={fqdn.origins}
                            placeholder="Origins"
                            onChange={(e: any) => setFqdn({ ...fqdn, origins: e.target.value })}
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <FormControl>
                    <HESPFormLabel label="LetsEncrypt" />
                    <SwitchWithTitle
                        title="Enable LetsEncrypt"
                        checked={tlaLetsEncrypt}
                        onChange={() => setTlaLetsEncrypt(!tlaLetsEncrypt)}
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <FormControl>
                    <HESPFormLabel label="On-prem" />
                    <SwitchWithTitle
                        title="Setup Datacenter as on-prem?"
                        checked={onPrem}
                        onChange={() => updateOnPremLabels()}
                    />
                </FormControl>
            </Grid>
            
            {!tlaLetsEncrypt && (
                <Grid item xs={12}>
                    <HESPFormLabel label="Certificates" />

                    <div className="flex flex-col w-full max-w-[600px] gap-2">
                        {tlaCertificates.map((certificate) => (
                            <div className="flex flex-row gap-4 w-full items-center">
                                <div className="w-full flex flex-col">
                                    <label className="font-semibold text-sm">Name</label>
                                    <TextField
                                        value={certificate.name}
                                        size="small"
                                        onChange={(e) =>
                                            setTlaCertificates(
                                                tlaCertificates.map((cert) => {
                                                    if (cert.id === certificate.id) {
                                                        return {
                                                            ...cert,
                                                            name: e.target.value,
                                                        };
                                                    }
                                                    return cert;
                                                }),
                                            )
                                        }
                                    />
                                </div>
                                <div className="w-full flex flex-col">
                                    <label className="font-semibold text-sm">Source</label>
                                    <TextField
                                        value={certificate.source}
                                        size="small"
                                        onChange={(e) =>
                                            setTlaCertificates(
                                                tlaCertificates.map((cert) => {
                                                    if (cert.id === certificate.id) {
                                                        return {
                                                            ...cert,
                                                            source: e.target.value,
                                                        };
                                                    }
                                                    return cert;
                                                }),
                                            )
                                        }
                                    />
                                </div>
                                <div>
                                    <IconButton
                                        onClick={() => {
                                            setTlaCertificates(
                                                tlaCertificates.filter((cert) => cert.id !== certificate.id),
                                            );
                                        }}
                                    >
                                        <DeleteForever />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                        <div className="flex flex-row justify-center mt-2">
                            <Button
                                startIcon={<Add />}
                                size="small"
                                onClick={() => {
                                    setTlaCertificates(tlaCertificates.concat([{ id: v4(), name: "", source: "" }]));
                                }}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                </Grid>
            )}
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <SectionTitle>Labels</SectionTitle>
            </Grid>
            <Grid item xs={12} md={existingId ? 12 : 6}>
                <FormControl fullWidth>
                    <TLALabelsManager
                        labels={labels}
                        onChangeLabel={(idx: string, val: string) => onChangeLabel(idx, val)}
                        onRemoveLabel={(idx: string) => onRemoveLabel(idx)}
                        regex={regex}
                    />
                </FormControl>
                <FormControl fullWidth style={{ display: "flex", justifyContent: "center" }}>
                    <Button
                        color="secondary"
                        startIcon={<Add />}
                        onClick={() => {
                            const copyLabels = labels;
                            copyLabels[v4()] = "";
                            setLabels({ ...copyLabels });
                            setLabelsOk(validLabels(Object.values(copyLabels)));
                        }}
                    >
                        Add label
                    </Button>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Override" />
                    <JSONInput
                        input={override}
                        onChange={(input: string) => setOverride(input)}
                        placeholder="Override object"
                    />
                    {override !== "" && !isValidJSON(override) && (
                        <Typography variant="subtitle2" color="error">
                            Invalid JSON object
                        </Typography>
                    )}
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                    <Button
                        onClick={onSubmitClick}
                        variant="contained"
                        size="large"
                        disabled={loadingCreate || loadingDatacenter || !isValid() || !labelsOk}
                    >
                        {loadingCreate || loadingDatacenter
                            ? existingId
                                ? "Updating"
                                : `Creating...`
                            : existingId
                            ? "Update"
                            : `Create`}
                    </Button>
                </div>
            </Grid>
            <ToastContainer />
        </Grid>
    );
}
